<script setup lang="ts">
import type {
  BookingDateTimeModel,
  StopHint,
  StopTimeObject,
} from '@/models/advancedJobBooking';

import { computed } from 'vue';

import JobBookingWarningBanner from '@/components/banners/JobBookingWarningBanner.vue';
import TimeSlotSelectField from '@/components/fields/TimeSlotSelectField.vue';
import DatePickerField from '@/components/pickerFields/DatePickerField.vue';
import KTooltip from '@/components/tooltips/KTooltip.vue';

export interface Props {
  disabled?: boolean;
  hints?: StopHint[];
  minTime?: StopTimeObject | null;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  hints: () => [],
  minTime: undefined,
});

const emit = defineEmits<{
  openTimeline: [event: Event];
}>();

const model = defineModel<BookingDateTimeModel>({
  default: () => ({
    start_date: null,
    end_date: null,
    link_dates: false,
    time_mode: 'any',
    am_pm: null,
    time_from: null,
    time_to: null,
  }),
});

function handleStartDate(value: string | null | undefined): void {
  const v = value ?? null;
  model.value = {
    ...model.value,
    start_date: v,
    ...(model.value.link_dates ? { end_date: v } : {}),
  };
}

function handleEndDate(value: string | null | undefined): void {
  model.value = { ...model.value, end_date: value ?? null };
}

function toggleLinkDates(): void {
  const linked = !model.value.link_dates;
  model.value = {
    ...model.value,
    link_dates: linked,
    end_date: linked ? (model.value.start_date ?? null) : model.value.end_date,
  };
}

function handleTimePill(pill: 'AM' | 'PM' | 'Custom'): void {
  if (props.disabled || !model.value.start_date) return;
  if (pill === 'AM') {
    const active =
      model.value.time_mode === 'ampm' && model.value.am_pm === 'AM';
    model.value = {
      ...model.value,
      time_mode: active ? 'any' : 'ampm',
      am_pm: active ? null : 'AM',
    };
  } else if (pill === 'PM') {
    const active =
      model.value.time_mode === 'ampm' && model.value.am_pm === 'PM';
    model.value = {
      ...model.value,
      time_mode: active ? 'any' : 'ampm',
      am_pm: active ? null : 'PM',
    };
  } else {
    const active = model.value.time_mode === 'specific';
    model.value = {
      ...model.value,
      time_mode: active ? 'any' : 'specific',
      am_pm: null,
    };
  }
}

const pillsDisabled = computed<boolean>(
  () => props.disabled || !model.value.start_date,
);

function handleTimeFrom(v: StopTimeObject | null): void {
  model.value = { ...model.value, time_from: v };
}

function handleTimeTo(v: StopTimeObject | null): void {
  model.value = { ...model.value, time_to: v };
}
</script>

<template>
  <fieldset
    :class="$style.stopBookingDateTimeFieldset"
    data-testid="stopBookingDateTimeFieldset"
  >
    <span :class="$style.label">Date</span>

    <div :class="$style.dateRow">
      <DatePickerField
        :model-value="model.start_date ?? null"
        label="From"
        :disabled="props.disabled"
        auto-apply
        @update:model-value="handleStartDate"
      />
      <div :class="$style.linkWrap">
        <span :class="$style.linkConnector" />
        <v-btn
          :icon="model.link_dates ? 'mdi-link-variant' : 'mdi-link-variant-off'"
          size="small"
          variant="text"
          :color="model.link_dates ? 'primary' : undefined"
          :disabled="props.disabled || !model.start_date"
          :class="$style.linkBtn"
          @click="toggleLinkDates"
        />
        <span :class="$style.linkConnector" />
      </div>
      <DatePickerField
        :model-value="model.end_date ?? null"
        label="To"
        :disabled="props.disabled || model.link_dates"
        auto-apply
        @update:model-value="handleEndDate"
      />
    </div>

    <div :class="$style.timeWindowRow">
      <span :class="$style.timeWindowLabel">Time window</span>
      <div :class="$style.timePills">
        <KTooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <button
              type="button"
              v-bind="tooltipProps"
              :class="[
                $style.pill,
                model.time_mode === 'ampm' &&
                  model.am_pm === 'AM' &&
                  $style.pillActive,
                pillsDisabled && $style.pillDisabled,
              ]"
              @click="handleTimePill('AM')"
            >
              AM
            </button>
          </template>
          <span>
            Presets a time range (9:00 AM &ndash; 12:00 PM); you can adjust it
            freely afterwards
          </span>
        </KTooltip>
        <KTooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <button
              type="button"
              v-bind="tooltipProps"
              :class="[
                $style.pill,
                model.time_mode === 'ampm' &&
                  model.am_pm === 'PM' &&
                  $style.pillActive,
                pillsDisabled && $style.pillDisabled,
              ]"
              @click="handleTimePill('PM')"
            >
              PM
            </button>
          </template>
          <span>
            Presets a time range (12:00 PM &ndash; 5:00 PM); you can adjust it
            freely afterwards
          </span>
        </KTooltip>
        <KTooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <button
              type="button"
              v-bind="tooltipProps"
              :class="[
                $style.pill,
                model.time_mode === 'specific' && $style.pillActive,
                pillsDisabled && $style.pillDisabled,
              ]"
              @click="handleTimePill('Custom')"
            >
              Custom
            </button>
          </template>
          <span>Set a custom time range for a specific delivery time</span>
        </KTooltip>
      </div>
    </div>

    <div v-if="model.time_mode === 'ampm'" :class="$style.timeRow">
      <TimeSlotSelectField
        :model-value="model.time_from ?? null"
        label="From"
        readonly
      />
      <TimeSlotSelectField
        :model-value="model.time_to ?? null"
        label="To"
        readonly
      />
    </div>

    <div v-if="model.time_mode === 'specific'" :class="$style.timeRow">
      <TimeSlotSelectField
        :model-value="model.time_from ?? null"
        label="From"
        :disabled="props.disabled"
        :min-time="props.minTime"
        @update:model-value="handleTimeFrom"
      />
      <TimeSlotSelectField
        :model-value="model.time_to ?? null"
        label="To"
        :disabled="props.disabled"
        :min-time="model.time_from ?? null"
        @update:model-value="handleTimeTo"
      />
    </div>

    <template v-for="(hint, i) in props.hints" :key="i">
      <JobBookingWarningBanner
        v-if="hint.type === 'warning'"
        :message="hint.text"
      />
      <JobBookingWarningBanner
        v-else
        type="info"
        clickable
        :message="`Available ${hint.text}`"
        @click="emit('openTimeline', $event)"
      />
    </template>
  </fieldset>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.stopBookingDateTimeFieldset {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.$color-text-primary;
  }
  .dateRow {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
  }
  .linkWrap {
    display: flex;
    align-items: center;
    // DatePickerField reserves 22px below for its details row; offset the link
    // group up by the same amount so it centers on the date input boxes.
    margin-bottom: 22px;
  }
  .linkConnector {
    width: 12px;
    height: 2px;
    background-color: colors.$color-gray-300;
  }
  .linkBtn {
    color: colors.$color-text-secondary !important;
    margin: 0;
  }
  .timeWindowRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .timeWindowLabel {
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.$color-text-primary;
    white-space: nowrap;
  }
  .timePills {
    display: flex;
    gap: 0.375rem;
  }
  .pill {
    padding: 0.25rem 0.875rem;
    border-radius: 6px;
    border: 1px solid colors.$color-gray-300;
    background: colors.$color-white;
    font-size: 0.8125rem;
    font-weight: 500;
    color: colors.$color-text-primary;
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s, color 0.15s;

    &:hover:not(.pillDisabled):not(.pillActive) {
      border-color: bp.$color-brand-400;
      color: bp.$color-brand-700;
    }
  }
  .pillActive {
    background-color: bp.$color-brand-700;
    border-color: bp.$color-brand-700;
    color: colors.$color-white;
  }
  .pillDisabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .timeRow {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
</style>
